<template>
  <div
    class="cma-reading-card"
    :class="[
      cardStateClass,
      { 'cma-reading-card--active': active },
    ]"
  >
    <div class="cma-reading-card__header">
      <div class="cma-reading-card__title-group">
        <img
          v-if="card.icon"
          class="cma-reading-card__icon"
          :src="card.icon"
          alt=""
          aria-hidden="true"
        >
        <h2 class="cma-reading-card__title">{{ title }}</h2>
      </div>
      <span
        v-if="loading"
        class="cma-skeleton-bar cma-reading-card__badge-skeleton"
        aria-hidden="true"
      />
      <!-- sem módulo não há status: o badge sairia como um "normal" que
           ninguém mediu (ver cardsConfig.js, buildCards) -->
      <span
        v-else-if="!unavailable"
        class="cma-reading-card__badge"
      >{{ $t(statusLabelKey) }}</span>
    </div>

    <div class="cma-reading-card__body">
      <p
        v-if="unavailable && !loading"
        class="cma-reading-card__no-data"
      >{{ $t('cma.cards.no-data') }}</p>
      <template v-else-if="!loading">
        <cma-field-row
          v-for="(row, index) in displayRows"
          :key="index"
          :label="row.label"
          :value="row.value"
          :unit="row.unit"
          :stacked="row.stacked"
          :status="row.status"
        />
      </template>
      <!-- Skeleton: mesmo número de linhas que o conteúdo real vai ter (ver
           SKELETON_ROW_COUNTS_BY_CONTENT_ID em cardsConfig.js), para o card
           não mudar de altura quando a leitura chega. -->
      <template v-else>
        <div
          v-for="n in skeletonRows"
          :key="n"
          class="cma-reading-card__row-skeleton"
        >
          <span
            class="cma-skeleton-bar cma-reading-card__row-skeleton-label"
            aria-hidden="true"
          />
          <span
            class="cma-skeleton-bar cma-reading-card__row-skeleton-value"
            aria-hidden="true"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getStatusLabelKey } from '@/domain/data/cma/statusLabels';
import CmaFieldRow from './CmaFieldRow';

const FANS_KEY = 'cma.fields.fans';

// Medições que, dentro da linha de uma fase, precisam de uma abreviação
// própria antes do valor (ex.: "Tg: 0.7%") — a 1ª medição (capacitância) não
// leva rótulo, seu valor já é óbvio por ser o primeiro da linha, igual à
// referência ("FA: 72 pF | Tg: 0.7% | Fuga: 35 mA").
const PHASE_SUB_LABEL_KEYS = {
  'cma.fields.tangent-delta': 'cma.fields.tangent-delta-short',
  'cma.fields.leakage-current': 'cma.fields.leakage-current-short',
};

// Junta valor+unidade em texto ("72 pF" / "0.7%" — sem espaço antes do "%",
// igual à referência), prefixando com a abreviação da medição quando houver
// uma (ver PHASE_SUB_LABEL_KEYS). Usado para compor as linhas agrupadas por
// fase (ex.: "72 pF | Tg: 0.7% | Fuga: 35 mA").
function formatValue(row, translate) {
  const amount = row.unit === '%' ? `${row.value}${row.unit}` : `${row.value} ${row.unit}`.trim();
  const subLabelKey = PHASE_SUB_LABEL_KEYS[row.labelKey];
  return subLabelKey ? `${translate(subLabelKey)}: ${amount}` : amount;
}

// Agrupa linhas que representam a mesma medição em fases diferentes (FA/FB/FC)
// numa única linha por fase, unindo os valores com "|" — evita que um card com
// 3 fases x N medições vire uma lista longa demais (spec correções — fidelidade
// ao layout de referência, onde cada fase ocupa 1 linha só).
function groupRowsByPhase(rows, translate) {
  const phases = [...new Set(rows.map(row => row.labelParams.phase))];
  return phases.map(phase => ({
    label: phase,
    value: rows
      .filter(row => row.labelParams.phase === phase)
      .map(row => formatValue(row, translate))
      .join(' | '),
    unit: '',
    // Igual à referência: rótulo e valor na mesma linha (ex.: "FA  72 pF |
    // Tg: 0.7% | Fuga: 35 mA"), não empilhados — o card de buchas agora é
    // largo o suficiente para isso caber sem quebrar.
    stacked: false,
  }));
}

export default {
  name: 'CmaReadingCard',
  components: {
    CmaFieldRow,
  },
  props: {
    card: {
      type: Object,
      required: true,
    },
    // Card selecionado (clicado) no dashboard — ver `selectedCardId` em
    // CmaDashboard.vue. Mantém a mesma borda de destaque do hover, mas
    // persistente, mesmo com o mouse fora do card.
    active: {
      type: Boolean,
      default: false,
    },
    // true enquanto a leitura desse card ainda não chegou (ver CmaDashboard.vue,
    // `!readings`) — troca o badge e as linhas por um skeleton, mantendo o
    // mesmo título (estático, já conhecido antes da resposta do backend, ver
    // cardsConfig.js) e o mesmo chrome do card (borda/padding/hover).
    loading: {
      type: Boolean,
      default: false,
    },
    // Número de linhas do skeleton — ver `card.skeletonRows` em
    // buildSkeletonCards (cardsConfig.js), espelha o número real de linhas
    // desse conteúdo para o card não mudar de altura ao carregar.
    skeletonRows: {
      type: Number,
      default: 3,
    },
  },
  computed: {
    displayRows() {
      // "Buchas" agrupa, por fase, as 3 medições (capacitância | tangente
      // delta | corrente de fuga) numa única linha por fase — igual à
      // referência (1 linha por fase, sem coluna extra). Usa `contentId`
      // (não `id`, que é a posição fixa na tela — ver CONTENT_OVERRIDES em
      // cardsConfig.js), porque a leitura exibida num card pode não ser a da
      // sua própria posição.
      if (this.card.contentId === 'bushings') return this.groupBushingsRows();
      if (this.card.contentId === 'cooling') return this.groupCoolingRows();
      return this.card.rows.map(row => this.toDisplayRow(row));
    },
    statusLabelKey() {
      return getStatusLabelKey(this.card.status);
    },
    /** Posição cujo módulo não veio do banco (ver cardsConfig.js, buildCards):
     *  o card continua na grade, apagado, em vez de sumir. */
    unavailable() {
      return Boolean(this.card.unavailable);
    },
    cardStateClass() {
      if (this.loading) return 'cma-reading-card--loading';
      if (this.unavailable) return 'cma-reading-card--unavailable';
      return `cma-reading-card--${this.card.status}`;
    },
    title() {
      // `card.number` só ordena internamente (badge "N." removido do
      // título por pedido de produto — não deve aparecer para o usuário).
      // `card.title` (nome do backend, já internacionalizado — ver
      // cmaCardsAdapter.js/cardsConfig.js) tem prioridade; `titleKey`
      // (tradução estática local) é só o fallback pra quando não há —
      // skeleton (carregando), card sem módulo no ativo (`unavailable`) ou
      // sem fonte no backend (Resfriamento).
      return this.card.title || this.$t(this.card.titleKey);
    },
  },
  methods: {
    translateLabel(row) {
      return this.$t(row.labelKey, row.labelParams);
    },
    // `row.labelKey` só existe nas linhas "legadas" (Buchas, Resfriamento —
    // ver displayRows) que ainda mapeiam rótulo/valor por chave de i18n.
    // Toda linha dinâmica (ver cmaCardsAdapter.js, buildDynamicRows) já traz
    // `label` pronto (o `nome` do campo, traduzido pelo backend) e não passa
    // por aqui.
    toDisplayRow(row) {
      if (row.labelKey) {
        return {
          label: this.translateLabel(row), value: row.value, unit: row.unit, stacked: false,
        };
      }
      // Linhas agrupadas por fase (vários campos viraram 1 linha só —
      // `codigo: null`, ver buildDynamicRows) empilham rótulo/valor (mesmo
      // tratamento que "Enrolamentos"/"Tensão do Motor" já tinham) e ganham
      // a cor do status do próprio card quando ele não está normal — mesmo
      // destaque que já existia para a tensão do motor do comutador em
      // atenção.
      const agrupada = row.codigo === null;
      return {
        label: row.label,
        // Bolsa é a única linha dinâmica cujo valor é um código interno de
        // status (não texto pronto) — ver `isStatus` em buildBagCard.
        value: row.isStatus ? this.$t(getStatusLabelKey(row.value)) : row.value,
        unit: row.unit,
        stacked: agrupada,
        status: agrupada && this.card.status !== 'normal' ? this.card.status : null,
      };
    },
    // `card.rows` de Buchas é uma lista mista (ver buildBushingsRows em
    // cardsConfig.js): as linhas conhecidas (`labelKey`) continuam agrupadas
    // por fase, como sempre; um campo novo do módulo BM (sem `labelKey`, já
    // no formato dinâmico dos outros cards) vem depois, 1 linha cada, sem
    // agrupamento por fase — só o padrão genérico de toDisplayRow.
    groupBushingsRows() {
      const knownRows = this.card.rows.filter(row => row.labelKey);
      const extraRows = this.card.rows.filter(row => !row.labelKey);
      return [
        ...groupRowsByPhase(knownRows, this.$t),
        ...extraRows.map(row => this.toDisplayRow(row)),
      ];
    },
    // A linha de ventiladores vira "3 de 3 ativos": o texto depende de
    // tradução (ver cma.fields.fans-active), então é montado aqui e não em
    // cardsConfig.js, que é dado puro e não enxerga o $t.
    groupCoolingRows() {
      const { rows } = this.card;
      const fansRow = rows.find(row => row.labelKey === FANS_KEY);
      return rows.map((row) => {
        if (row !== fansRow) return this.toDisplayRow(row);
        return {
          label: this.translateLabel(row),
          value: this.$t('cma.fields.fans-active', row.labelParams),
          unit: '',
          stacked: false,
        };
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

// Faixa cinza que varre da esquerda pra direita (ver .cma-skeleton-bar
// abaixo) — mesma animação usada em CmaDashboard.vue (duplicada de
// propósito: são estilos `scoped`, sem folha de estilo compartilhada entre
// os dois componentes).
@keyframes cma-skeleton-shimmer
  0%
    background-position: -150% 0
  100%
    background-position: 150% 0

.cma-skeleton-bar
  // Tons próximos do fundo do próprio card (--color-background-regular/
  // -hover) em vez de --color-background-dark/--color-border-dark: no tema
  // escuro esse par ia de quase preto (#151515) a cinza médio (#4c4c4c),
  // um contraste forte demais contra o card (#212121) — pedido explícito
  // pra ficar sutil, quase da cor do fundo.
  $base: var(--color-background-regular)
  $highlight: var(--color-background-regular-hover)
  display: inline-block
  border-radius: 4px
  background: linear-gradient(90deg, $base 25%, $highlight 50%, $base 75%)
  background-size: 200% 100%
  animation: cma-skeleton-shimmer 1.6s ease-in-out infinite

.cma-reading-card
  background-color: var(--color-background-light)
  border: 1px solid var(--color-border-dark)
  border-radius: 8px
  padding: 0.75rem 1rem
  height: fit-content
  // Clicável: dá zoom na peça 3D correspondente (ver onCardClick em
  // CmaDashboard.vue)
  cursor: pointer

  // Acompanha a largura maior do card nessa faixa (ver &__card em
  // CmaDashboard.vue) numa tela de TV (~1700px+, ex.: 42" em 1920×1080).
  +media-min-3xl()
    padding: 1rem 1.35rem

  +media-min-4xl()
    padding: 1.2rem 1.6rem

  /* Tela larga e baixa (ver media-short em mobile-first.sass): os degraus
   * acima sao de largura, entao uma ultrawide 2560x1080 recebe o padding de
   * monitor grande sem ter a altura dele — e sao 4 cards por coluna. Aqui a
   * folga interna e o icone cedem, na ordem: primeiro o respiro, depois o
   * tamanho do icone, que e o elemento mais alto do cabecalho. */
  +media-short()
    padding: 0.5rem 0.85rem

    .cma-reading-card__icon
      height: 2.4em

  +media-very-short()
    padding: 0.4rem 0.75rem

    .cma-reading-card__icon
      height: 2em

  // A borda (e a linha de conexão — ver CmaDashboard.vue) só ganha destaque
  // no hover, em todos os cards (mesma cor, independente do status) — para
  // não deixar a tela poluída de cor permanentemente: o badge já comunica o
  // status o tempo todo. O card selecionado (clique — ver prop `active`)
  // ganha o mesmo destaque, mas persistente, mesmo com o mouse fora dele.
  &:hover,
  &--active
    border-color: var(--color-warning)
    box-shadow: 0 0 0 1px var(--color-warning)

  &--normal .cma-reading-card__badge
    color: var(--color-success)

  &--attention .cma-reading-card__badge
    color: var(--color-warning)

  &--critical .cma-reading-card__badge
    color: var(--color-danger)

  // Card em atenção: borda e título ficam amarelos (mesma cor de
  // --color-warning já usada no hover/seleção genéricos acima, ver &:hover,
  // &--active) permanentemente, não só ao interagir — daí não precisar de
  // variante :hover/.active própria aqui, a cor já bate com a do destaque
  // genérico.
  &--attention
    border-color: var(--color-warning)
    // Anel fino (igual ao hover/seleção genéricos) + uma sombra só embaixo
    // (offset-y positivo, sem blur nos outros lados, spread negativo pra não
    // vazar de volta pros lados) — o `blur` já esmaece a cor sozinho, sem
    // precisar de rgba/color-mix (o projeto usa LibSass, que não suporta a
    // sintaxe `color-mix(in ...)`). O shadow em si é o destaque extra pedido
    // para atenção/crítico, além da cor da borda/badge/título que já existia.
    box-shadow: 0 0 0 1px var(--color-warning), 0 10px 16px -6px var(--color-warning)

  &--attention .cma-reading-card__title
    color: var(--color-warning)

  // Card crítico: borda e título ficam vermelhos permanentemente — inclusive
  // no hover e quando selecionado (`--active`), que para os demais cards
  // pintam a borda de laranja (ver &:hover, &--active acima). Por isso estas
  // regras vêm DEPOIS: mesma especificidade (2 classes cada), a última
  // declarada no arquivo vence.
  &--critical,
  &--critical:hover,
  &--critical.cma-reading-card--active
    border-color: var(--color-danger)
    box-shadow: 0 0 0 1px var(--color-danger), 0 10px 16px -6px var(--color-danger)

  &--critical .cma-reading-card__title
    color: var(--color-danger)

  // Sem interação enquanto carrega: nada pra focar/dar zoom ainda (ver
  // onCardClick, guardado por `!readings` em CmaDashboard.vue), então o
  // destaque de hover (ver &:hover, &--active acima) some para não sugerir
  // que o card já é clicável.
  &--loading
    cursor: default

    &:hover
      border-color: var(--color-border-dark)
      box-shadow: none

  /* Posicao sem modulo no banco: o card fica na grade, mas recuado — fundo
   * rebaixado, borda tracejada e conteudo esmaecido, sem badge e sem valores.
   * Nao e clicavel (nao ha peca 3D para enquadrar) nem ganha o destaque de
   * hover, para nao se oferecer como se tivesse dado atras. */
  &--unavailable
    background-color: transparent
    border-style: dashed
    box-shadow: none
    cursor: default
    opacity: 0.55

    &:hover,
    &.cma-reading-card--active
      border-color: var(--color-border-dark)
      box-shadow: none

  &--unavailable &__title
    font-weight: 400

  &--unavailable &__icon
    opacity: 0.45

  &__no-data
    margin: 0
    color: var(--font-color-secondary)
    font-size: 0.75rem
    font-style: italic

    +media-min-3xl()
      font-size: 0.95rem

    +media-min-4xl()
      font-size: 1.1rem

  &__header
    display: flex
    // Era `baseline`: com o ícone maior (ver &__icon abaixo), a baseline do
    // <img> passou a ficar bem mais baixa que a do texto, empurrando o
    // badge de status (que também está nesta linha) pra baixo junto. `center`
    // alinha pelo meio da altura de cada item, então o badge some do lugar
    // errado independente do tamanho do ícone.
    align-items: center
    justify-content: space-between
    gap: 0.75rem
    margin-bottom: 0.35rem

  // Ícone + título agrupados para o "space-between" do &__header acima
  // continuar jogando só o badge de status pro canto oposto, sem contar o
  // ícone como um 3º item do flex.
  &__title-group
    display: flex
    align-items: center
    gap: 0.4rem
    min-width: 0

  // Ícone de identificação do card (ver cardsConfig.js, `icon`) — opcional:
  // nem todo card tem um ainda (ver `v-if` no template). Tamanho em `em`
  // para crescer nos mesmos degraus responsivos do título (ver &__title
  // abaixo), em vez de um valor fixo em px que ficaria pequeno demais numa
  // TV ou grande demais num card compacto. 1.7em estava quase invisível
  // (referência de design: ícone ~3.3x a altura da fonte do título, não
  // ~1.7x) — subiu para 3em, escalando junto nos mesmos breakpoints.
  &__icon
    height: 3em
    width: auto
    flex-shrink: 0

    +media-min-3xl()
      height: 3.6em

    +media-min-4xl()
      height: 4.2em

  &__title
    color: var(--color-secondary)
    // Maior que o valor das linhas abaixo (ver CmaFieldRow.vue, &__value —
    // mesmo tamanho de fonte que este título tinha antes): sem uma face 700
    // de verdade no projeto (só existem 300/400/600 — ver fonts.sass), título
    // e valor em "font-weight: 700" caem na MESMA face 600, com o mesmo peso
    // visual — e o valor, sendo números densos e com tracking mais apertado,
    // ainda parecia mais pesado que o título. Maior tamanho é o jeito real de
    // dar hierarquia aqui, mesmo princípio de h1 > h2 > corpo do resto do
    // sistema (ver base.sass).
    font-size: 0.95rem
    font-weight: 700
    letter-spacing: 0.05em
    text-transform: uppercase
    margin: 0

    +media-min-3xl()
      font-size: 1.15rem

    +media-min-4xl()
      font-size: 1.35rem

  &__badge
    font-size: 0.8rem
    font-weight: 700
    letter-spacing: 0.05em
    text-transform: uppercase
    white-space: nowrap

    +media-min-3xl()
      font-size: 1rem

    +media-min-4xl()
      font-size: 1.2rem

  &__body
    display: flex
    flex-direction: column

  // Substitui o badge de status (ver &__badge acima) — mesma altura de
  // linha aproximada, só sem o texto.
  &__badge-skeleton
    width: 3.25rem
    height: 0.7rem

    +media-min-3xl()
      width: 3.75rem
      height: 0.85rem

    +media-min-4xl()
      width: 4.25rem
      height: 1rem

  // Substitui uma CmaFieldRow (ver &__body acima e CmaFieldRow.vue) —
  // mesmo espaçamento vertical, rótulo mais curto que o valor para não
  // ficarem visualmente idênticos.
  &__row-skeleton
    display: flex
    align-items: baseline
    justify-content: space-between
    gap: 0.4rem
    padding: 0.25rem 0

  &__row-skeleton-label
    width: 35%
    height: 0.7rem

    +media-min-3xl()
      height: 0.9rem

    +media-min-4xl()
      height: 1.1rem

  &__row-skeleton-value
    width: 45%
    height: 0.7rem

    +media-min-3xl()
      height: 0.9rem

    +media-min-4xl()
      height: 1.1rem
</style>
